import { Box, Collapse, Grid, Stack, Tooltip, Typography } from '@mui/material'
import {
  ManagerResearchVisitEvent,
  ResearchArea as ResearchAreaType,
  UserGroups,
} from '@/types'
import { useTranslation } from 'react-i18next'
import {
  AuthorizationGuard,
  CardAttribute,
  SharepointUrlLink,
} from '@/components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMemo, useState } from 'react'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { formatMinutesDuration } from '@/utils/date'
import { ResearchAreaEvents } from '@/features/projectDetail/ResearchAreas/components/ResearchAreaEvents'
import { usePlanningsEventsManager } from '@/hooks'
import { getUniqueResearchers } from '@/utils'
import { PreviewResearchAreaGraphics } from '@/components/forms/ResearchAreaForm/PreviewResearchAreaGraphics'

export type CardLayoutProps = {
  area: ResearchAreaType
  open?: boolean
  projectId: number
}

export const ResearchArea = ({
  area,
  open,
  projectId,
}: CardLayoutProps): JSX.Element => {
  const { t } = useTranslation('projects')
  const [isOpen, setIsOpen] = useState(open)
  const { data: projectEvents, isLoading: projectEventsLoading } =
    usePlanningsEventsManager({
      limit: 1000,
      offset: 0,
      project: projectId.toString(),
    })
  const areaEvents = useMemo<ManagerResearchVisitEvent[]>(
    () =>
      projectEvents?.results.filter(
        (event) => event.research_area_id === area.id
      ) ?? [],
    [projectEvents]
  )

  const handleClick = () => {
    setIsOpen(!isOpen)
  }

  const totalDuration = area.protocol_events_total_duration

  const Warning = (
    <Tooltip title={t('areas.needPlanEvents')}>
      <WarningAmberIcon color="warning" />
    </Tooltip>
  )

  const showWarning =
    area.protocol_events_count !== undefined &&
    area.events_count !== undefined &&
    area.protocol_events_count > area.events_count

  const uniqueResearchers = area.research_area_protocols?.length
    ? getUniqueResearchers(area.research_area_protocols)
    : []

  return (
    <>
      <Stack
        sx={{ ':hover': { cursor: 'pointer' } }}
        onClick={handleClick}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        pt={2}
      >
        <Typography variant="h5">
          {`${t('areas.researchAreaNumber', { areaNumber: area.id })} - ${
            area.location_name
          }`}
        </Typography>
        <Stack direction="row" spacing={4}>
          {showWarning && Warning}
          <Box color="secondary.main">
            {!isOpen ? (
              <FontAwesomeIcon icon={faChevronDown} />
            ) : (
              <FontAwesomeIcon icon={faChevronUp} />
            )}
          </Box>
        </Stack>
      </Stack>

      <Collapse in={isOpen} timeout="auto">
        <Grid container={true} rowSpacing={5} columnSpacing={4} mb={3}>
          <Grid item={true} xs={6} sm={3}>
            <CardAttribute
              title={t('areas.areaNumber')}
              value={area.id ?? '-'}
            />
          </Grid>
          <Grid item={true} xs={6} sm={3}>
            <CardAttribute
              title={t('areas.clientAreaNumber')}
              value={!!area.client_area_number ? area.client_area_number : '-'}
            />
          </Grid>

          <AuthorizationGuard
            allowedGroups={[UserGroups.MANAGER, UserGroups.EMPLOYEE]}
            redirect={false}
          >
            <Grid item={true} xs={6} sm={3}>
              <CardAttribute
                title={t('areas.amountHours')}
                value={
                  totalDuration ? formatMinutesDuration(totalDuration) : '-'
                }
              />
            </Grid>
          </AuthorizationGuard>

          <Grid item={true} sm={6}>
            <CardAttribute
              title={t('areas.researchers')}
              value={
                uniqueResearchers.length
                  ? uniqueResearchers.map(
                      ({ first_name, last_name }, i) =>
                        `${i > 0 ? ', ' : ''}${first_name} ${last_name}`
                    )
                  : t('areas.noResearchers')
              }
            />
          </Grid>

          <Grid item={true} sm={12}>
            <AuthorizationGuard
              allowedGroups={[UserGroups.MANAGER, UserGroups.EMPLOYEE]}
              redirect={false}
            >
              <CardAttribute
                title={t('areas.address')}
                value={area.location_name ?? '-'}
              />
            </AuthorizationGuard>
            {area.graphics.length > 0 && (
              <Box>
                <PreviewResearchAreaGraphics
                  graphics={area.graphics}
                  projectId={projectId}
                  areaId={area.id}
                  areaAddress={{
                    locationName: area.location_name ?? '',
                    locationLong: area.location_long,
                    locationLat: area.location_lat,
                  }}
                />
              </Box>
            )}
          </Grid>
          <Grid item={true} sm={12}>
            <CardAttribute
              title={t('areas.notes')}
              value={!!area.notes ? area.notes : '-'}
            />
          </Grid>
          {area.report_sharepoint_url && (
            <Grid item={true} sm={12}>
              <CardAttribute
                title={t('areas.reportSharepointUrl')}
                value={
                  <>
                    <AuthorizationGuard
                      allowedGroups={[UserGroups.CLIENT]}
                      redirect={false}
                      fallback={
                        <SharepointUrlLink
                          sharepointUrl={area.report_sharepoint_url}
                        />
                      }
                    >
                      {areaEvents.length !== 0 &&
                        areaEvents.every(
                          (ev) =>
                            ev.is_event_approved ||
                            ev.research_visit_log?.status ===
                              'extra_event_requested'
                        ) && (
                          <SharepointUrlLink
                            sharepointUrl={area.report_sharepoint_url}
                          />
                        )}
                    </AuthorizationGuard>
                  </>
                }
              />
            </Grid>
          )}
        </Grid>

        {areaEvents.length !== 0 && (
          <Typography py={3} variant="h6">
            {t('areas.events')}
          </Typography>
        )}

        <AuthorizationGuard
          allowedGroups={[UserGroups.CLIENT]}
          redirect={false}
        >
          <Typography>{t('areas.planningDisclaimer')}</Typography>
        </AuthorizationGuard>

        <ResearchAreaEvents
          researchArea={area}
          events={areaEvents}
          isLoading={projectEventsLoading}
          canGoToDetail={true}
        />
      </Collapse>
    </>
  )
}
